import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type JSX } from "react";

import { useCan } from "../../../auth/permissions.js";
import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../../components/bulk-selection/index.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { marketingApi } from "../client.js";
import { ErrorBanner, minutesAgo } from "../shared.js";
import type { Draft, Idea, Platform } from "../types.js";
import { DraftReviewDialog } from "../workbench/DraftReviewDialog.js";
import { PlatformSelect } from "./studioShared.js";

/**
 * Ideas — the agent's autonomous topic-suggestion feed. It keeps a pool of
 * "what's hot in the industry / strongest in our knowledge base" fresh; the
 * operator clicks Generate on one to turn it into a draft (reviewed in the
 * shared dialog) or Dismiss to suppress it. Acting on an idea removes it from
 * the list. This is the "AI suggests topics, you click yes" surface.
 *
 * Rows also carry a selection checkbox so the operator can clear out a batch
 * of off-target suggestions in one go — the multi-select reuses the shared
 * `bulk-selection` module (the same one the pending-approval queue uses) and
 * routes through the bulk confirm dialog.
 */
export function IdeasPage(): JSX.Element {
  const canEdit = useCan("marketing:edit");
  const qc = useQueryClient();
  const ideas = useQuery({
    queryKey: ["marketing", "ideas"],
    queryFn: () => marketingApi.listIdeas(60),
    refetchInterval: 60_000,
  });

  const [platformById, setPlatformById] = useState<Record<number, Platform>>({});
  const [dismissing, setDismissing] = useState<Idea | null>(null);
  const [bulkDismissOpen, setBulkDismissOpen] = useState(false);
  const [reviewDraft, setReviewDraft] = useState<Draft | null>(null);
  const [open, setOpen] = useState(false);
  const [brainstormBrief, setBrainstormBrief] = useState("");

  const ideaList = ideas.data ?? [];
  // Multi-select for bulk dismiss — same shared module the pending-approval
  // queue uses (shift-click range select, auto-prune of stale ids).
  const bulk = useBulkSelection(ideaList);

  const platformFor = (id: number): Platform => platformById[id] ?? "linkedin_post";

  // Manager-driven brainstorm: a free-text brief → a few topic ideas added to
  // the pool. Operator-requested, so it's exempt from the daily idea quota.
  const brainstorm = useMutation({
    mutationFn: (brief: string) => marketingApi.brainstormIdeas(brief, 4),
    onSuccess: (created: Idea[]) => {
      setBrainstormBrief("");
      qc.invalidateQueries({ queryKey: ["marketing", "ideas"] });
      qc.invalidateQueries({ queryKey: ["marketing", "economics"] });
      toast({
        title: `Added ${created.length} idea${created.length === 1 ? "" : "s"}`,
        description: "They're in the list below — pick one to generate.",
      });
    },
    onError: (err) =>
      toast({
        variant: "destructive",
        title: "Couldn't brainstorm",
        description:
          err instanceof ApiError ? err.message : "The agent didn't return ideas.",
      }),
  });

  const generate = useMutation({
    mutationFn: ({ id, platform }: { id: number; platform: Platform }) =>
      marketingApi.generateIdea(id, platform),
    onSuccess: (draft: Draft) => {
      setReviewDraft(draft);
      setOpen(true);
      qc.invalidateQueries({ queryKey: ["marketing", "ideas"] });
      qc.invalidateQueries({ queryKey: ["marketing", "queue"] });
      qc.invalidateQueries({ queryKey: ["marketing", "economics"] });
      toast({ title: "Draft created", description: "Review, edit, or approve it." });
    },
    onError: (err) =>
      toast({
        variant: "destructive",
        title: "Could not generate",
        description: err instanceof ApiError ? err.message : "The agent failed to draft this.",
      }),
  });

  // Bulk dismiss — suppress every selected idea in one call. Optimistic (the
  // rows vanish immediately) and surfaces a destructive toast on failure, like
  // the pending-approval bulk delete.
  const bulkDismiss = useMutation({
    mutationFn: (ids: number[]) => marketingApi.bulkDismissIdeas(ids),
    onMutate: async (ids: number[]) => {
      await qc.cancelQueries({ queryKey: ["marketing", "ideas"] });
      const prev = qc.getQueryData<Idea[]>(["marketing", "ideas"]);
      const dropping = new Set(ids);
      qc.setQueryData<Idea[]>(["marketing", "ideas"], (old) =>
        (old ?? []).filter((i) => !dropping.has(i.id)),
      );
      return { prev };
    },
    onError: (err, _ids, ctx) => {
      if (ctx?.prev) qc.setQueryData(["marketing", "ideas"], ctx.prev);
      toast({
        variant: "destructive",
        title: "Couldn't dismiss the ideas",
        description:
          err instanceof ApiError ? err.message : "Please try again.",
      });
    },
    onSuccess: (res, ids) => {
      bulk.clear();
      const n = res.dismissed_ids?.length ?? ids.length;
      toast({ title: `Dismissed ${n} idea${n === 1 ? "" : "s"}` });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["marketing", "ideas"] });
    },
  });

  // On-demand discovery — kick the trend-scan loop now instead of waiting for
  // its schedule. Fire-and-forget (202); fresh ideas surface on the next poll.
  // It runs through the same pause gate as the scheduled loop, so it's disabled
  // while the agent is paused (it would otherwise be skipped silently).
  const settings = useQuery({
    queryKey: ["marketing", "agent-settings"],
    queryFn: marketingApi.getAgentSettings,
  });
  const paused = settings.data?.paused ?? false;
  const discover = useMutation({
    mutationFn: () => marketingApi.runAgent("trend_scan"),
    onSuccess: () => {
      toast({
        title: "Looking for new topics",
        description: "Discovery started — fresh ideas will appear here shortly.",
      });
      // The scan runs async on the worker; nudge a refetch once it's had a
      // moment (the 60 s poll is the backstop).
      setTimeout(() => qc.invalidateQueries({ queryKey: ["marketing", "ideas"] }), 8_000);
    },
    onError: (err) =>
      toast({
        variant: "destructive",
        title: "Could not start discovery",
        description:
          err instanceof ApiError ? err.message : "The agent didn't accept the request.",
      }),
  });

  if (ideas.isError) return <ErrorBanner error={ideas.error} />;

  return (
    <Box sx={{ display: "grid", gap: 2 }} data-testid="marketing-ideas">
      <Card>
        <CardContent sx={{ px: 2.5, py: 2 }}>
          {canEdit && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                inputProps={{ "data-testid": "brainstorm-brief" }}
                label="Brainstorm ideas about…"
                placeholder="e.g. emergency mass notification for hospitals"
                value={brainstormBrief}
                onChange={(e) => setBrainstormBrief(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && brainstormBrief.trim() && !brainstorm.isPending) {
                    brainstorm.mutate(brainstormBrief.trim());
                  }
                }}
                disabled={brainstorm.isPending}
                sx={{ flex: 1, minWidth: 260 }}
              />
              <Button
                type="button"
                variant="contained"
                data-testid="brainstorm-go"
                disabled={brainstorm.isPending || brainstormBrief.trim().length === 0}
                onClick={() => brainstorm.mutate(brainstormBrief.trim())}
              >
                {brainstorm.isPending ? "Thinking…" : "Brainstorm"}
              </Button>
              <Tooltip
                title={
                  paused
                    ? "Resume the agent to run discovery"
                    : "Scan industry news + the knowledge base for fresh topics right now"
                }
              >
                <span>
                  <Button
                    type="button"
                    variant="outlined"
                    data-testid="ideas-discover-now"
                    disabled={paused || discover.isPending}
                    onClick={() => discover.mutate()}
                  >
                    {discover.isPending ? "Starting…" : "Find topics now"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
          {!ideas.data ? (
            <Box sx={{ display: "grid", gap: 1 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={84} />
              ))}
            </Box>
          ) : ideas.data.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: "text.secondary" }} data-testid="ideas-empty">
              No fresh ideas right now. The agent scans industry news + the
              knowledge base every couple of hours and new topics will appear
              here. You can always use <strong>Create New</strong> in the
              meantime.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              {canEdit && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BulkCheckbox
                    checked={bulk.allOnPageChecked}
                    indeterminate={bulk.someOnPageChecked}
                    onChange={(checked) => bulk.toggleAllOnPage(checked)}
                    ariaLabel="Select all ideas"
                  />
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    Select all
                  </Typography>
                </Box>
              )}
              {canEdit && (
                <BulkActionBar count={bulk.count} onClear={bulk.clear}>
                  <BulkButton
                    disabled={bulkDismiss.isPending}
                    onClick={() => setBulkDismissOpen(true)}
                  >
                    Dismiss {bulk.count}
                  </BulkButton>
                </BulkActionBar>
              )}
              {ideaList.map((idea) => (
                <Box
                  key={idea.id}
                  data-testid="idea-row"
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: bulk.isSelected(idea.id)
                      ? "action.selected"
                      : "surfaceSoft",
                  }}
                >
                  {canEdit && (
                    <BulkCheckbox
                      checked={bulk.isSelected(idea.id)}
                      onChange={(checked, opts) =>
                        bulk.setChecked(idea.id, checked, { shiftKey: opts.shiftKey })
                      }
                      ariaLabel={`Select idea ${idea.id}`}
                    />
                  )}
                  <Box sx={{ minWidth: 220, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{idea.topic}</Typography>
                      <Box
                        component="span"
                        sx={{
                          borderRadius: 999,
                          px: 1,
                          py: 0.25,
                          fontSize: 11,
                          fontWeight: 500,
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                          color: "primary.main",
                        }}
                      >
                        {idea.reason}
                      </Box>
                    </Box>
                    {idea.summary && (
                      <Typography
                        sx={{
                          mt: 0.5,
                          fontSize: 13,
                          color: "text.secondary",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {idea.summary}
                      </Typography>
                    )}
                    <Typography sx={{ mt: 0.5, fontSize: 11, color: "text.disabled" }}>
                      relevance {Math.min(100, Math.max(0, Math.round(idea.score * 100)))}% ·{" "}
                      {minutesAgo(idea.found_at)}m ago
                    </Typography>
                  </Box>
                  {canEdit && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PlatformSelect
                        value={platformFor(idea.id)}
                        onChange={(p) => setPlatformById((m) => ({ ...m, [idea.id]: p }))}
                        disabled={generate.isPending}
                        label="As"
                      />
                      <Button
                        type="button"
                        variant="contained"
                        size="small"
                        data-testid="idea-generate"
                        disabled={generate.isPending}
                        onClick={() => generate.mutate({ id: idea.id, platform: platformFor(idea.id) })}
                      >
                        {generate.isPending && generate.variables?.id === idea.id
                          ? "Generating…"
                          : "Generate"}
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        color="inherit"
                        size="small"
                        data-testid="idea-dismiss"
                        disabled={generate.isPending}
                        onClick={() => setDismissing(idea)}
                      >
                        Dismiss
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={dismissing != null}
        onClose={() => setDismissing(null)}
        title="Dismiss this idea?"
        description={
          dismissing
            ? `"${dismissing.topic}" won't be suggested again.`
            : undefined
        }
        confirmLabel="Dismiss"
        onConfirm={async () => {
          if (!dismissing) return;
          await marketingApi.dismissIdea(dismissing.id);
          setDismissing(null);
          qc.invalidateQueries({ queryKey: ["marketing", "ideas"] });
        }}
      />

      <ConfirmDialog
        isOpen={bulkDismissOpen}
        onClose={() => setBulkDismissOpen(false)}
        title={`Dismiss ${bulk.count} idea${bulk.count === 1 ? "" : "s"}?`}
        description="The selected ideas won't be suggested again."
        confirmLabel={`Dismiss ${bulk.count}`}
        onConfirm={async () => {
          await bulkDismiss.mutateAsync([...bulk.selected]);
          setBulkDismissOpen(false);
        }}
      />

      <DraftReviewDialog draft={reviewDraft} open={open} onClose={() => setOpen(false)} />
    </Box>
  );
}
